import { buildOnboardingWorkflowTrail } from "./workflowStepsBadge";
import { resolveChecklistDisplayLabel } from "./deskActionMatrix";

export const ONBOARDING_APPROVAL_LABEL = "Employee Onboarding Approval";
export const ONBOARDING_TASK_LABEL = "Onboarding Task";
export const ONBOARDING_REQUEST_LABEL = "Employee Onboarding";
export const ONBOARDING_INSIGHT_ACTION = "Onboarding";

export function mapApiStatusToDeskStatus(apiStatus, options = {}) {
  // Notify-only FYI stays Pending until the recipient acknowledges (No action required).
  if (options.notifyOnlyFyi) {
    return options.acknowledged ? "Approved" : "Pending";
  }
  if (apiStatus === "pending_approval") return "Pending";
  if (apiStatus === "completed") return "Approved";
  if (apiStatus === "rejected") return "Rejected";
  if (apiStatus === "draft") return "Pending";
  return "Pending";
}

export function mapOnboardingStatusToInsight(apiStatus, workflowSnapshot = {}) {
  if (apiStatus === "rejected") return "Rejected";
  if (apiStatus === "completed") return "Approved";
  if (apiStatus === "pending_approval") return "Pending";
  if (apiStatus === "draft") return "Pending";
  if (apiStatus === "approved") return "Pending";
  return "Pending";
}

export function resolveInsightPendingAt(row) {
  const status = row?.status;
  const snapshot = row?.workflowSnapshot || {};
  if (status === "draft") return "In progress";
  if (status === "pending_approval") {
    return row?.pendingLabel || row?.pendingRole || "Approver";
  }
  if (status === "completed") {
    if (snapshot.notifyOnlyAutoApproved) return "Auto approved (RM notified)";
    if (snapshot.approvalMethod === "auto_approved") return "Auto approved";
    return row?.completedAt
      ? new Date(row.completedAt).toLocaleString()
      : "Approved";
  }
  if (status === "rejected") return "Rejected";
  return "-";
}

export function buildOrgFormData(org = {}, candidateId = "") {
  return {
    candidateId: candidateId || org.candidateId || "",
    name: org.name || org.fullName || "",
    gender: org.gender || "",
    dateOfBirth: org.dateOfBirth || org.dob || "",
    branchId: org.branchId ?? "",
    departmentId: org.departmentId ?? "",
    subDepartmentId: org.subDepartmentId ?? "",
    designationId: org.designationId ?? "",
    gradeId: org.gradeId ?? "",
    categoryId: org.categoryId ?? "",
    reportingManagerId: org.reportingManagerId ?? "",
    branch: org.branchName || org.branch || "",
    department: org.departmentName || org.department || "",
    subDepartment: org.subDepartmentName || org.subDepartment || "",
    designation: org.designationName || org.designation || "",
    grade: org.gradeName || org.grade || "",
    category: org.categoryName || org.category || "",
    reportingManager: org.reportingManagerName || org.reportingManager || "",
    dateOfJoin: org.dateOfJoin || org.joiningDate || "",
    employmentType: org.employmentType || org.employeeType || "",
    employmentStatus: org.employmentStatus || "",
    mobileNumber: org.mobileNumber || org.mobile || org.phone || "",
    personalEmail: org.personalEmail || "",
    officialEmail: org.officialEmail || org.email || "",
    bloodGroup: org.bloodGroup || "",
    aadhaarNo: org.aadhaarNo || org.aadhaar || org.aadharNumber || "",
    panNo: org.panNo || org.pan || org.panNumber || "",
    policyName: org.onboardingPolicyName || "",
  };
}

/** Leave-style subset for Approvals / Activity Console — person lives on the card header. */
export function buildOnboardingDeskFormData(org = {}) {
  return {
    dateOfJoin: org.dateOfJoin || org.joiningDate || "",
    employmentType: org.employmentType || org.employeeType || "",
    department: org.departmentName || org.department || "",
    designation: org.designationName || org.designation || "",
    branch: org.branchName || org.branch || "",
    reportingManager: org.reportingManagerName || org.reportingManager || "",
    policyName: org.onboardingPolicyName || "",
  };
}

/** Collapse cloned checklist cards (same employee + task name + status). */
function checklistPersonKey(row) {
  const code = String(row?.employeeCode || row?.formData?.employeeCode || "")
    .trim()
    .toLowerCase();
  if (code && code !== "-") return `c:${code}`;
  const empPk = row?.linkedEmployeeId ?? (typeof row?.employeeId === "number" ? row.employeeId : null);
  if (empPk != null && String(empPk).trim() !== "") return `e:${empPk}`;
  const name = String(row?.userName || row?.candidateName || row?.formData?.employeeName || "")
    .trim()
    .toLowerCase();
  return `n:${name}`;
}

function checklistNameKey(row) {
  return String(
    row?.formData?.taskName || row?.checklistTask?.name || row?.checklistName || ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function checklistStatusKey(row) {
  const raw = String(row?.checklistTask?.status || row?.status || "").toLowerCase();
  if (raw === "done" || raw === "approved") return "approved";
  if (raw === "rejected" || raw === "sent_back") return "rejected";
  return "pending";
}

export function collapseDuplicateOnboardingChecklistRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return rows || [];
  const ts = (row) => {
    const raw = row?.updatedAt || row?.submittedAt || row?.checklistTask?.triggeredAt || 0;
    const n = typeof raw === "number" ? raw : Date.parse(raw);
    return Number.isFinite(n) ? n : 0;
  };
  const sorted = [...rows].sort((a, b) => ts(b) - ts(a));
  const seen = new Set();
  const out = [];
  for (const row of sorted) {
    const isChk =
      row?.deskMode === "checklist" ||
      row?.checklistTaskId != null ||
      row?.checklistTask != null ||
      String(row?.requestId || "").startsWith("onboarding_chk_");
    if (!isChk) {
      out.push(row);
      continue;
    }
    const key = `${checklistPersonKey(row)}|${checklistNameKey(row)}|${checklistStatusKey(row)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function mapOnboardingRequestToInsightItem(row, employeeLookup = {}) {
  const org = row.payload?.org || {};
  const name = org.name || org.fullName || "Candidate";
  const linkedEmployee = row.employeeId ? employeeLookup[row.employeeId] : null;
  const employeeCode =
    linkedEmployee?.empCode ||
    linkedEmployee?.emp_code ||
    (row.status === "completed" ? "-" : row.candidateId);
  const status = mapOnboardingStatusToInsight(row.status, row.workflowSnapshot);
  const pendingAt = resolveInsightPendingAt(row);

  return {
    id: `onboarding_insight_${row.id}`,
    action: ONBOARDING_INSIGHT_ACTION,
    employeeCode: employeeCode || row.candidateId || "-",
    employeeName: name,
    employee: employeeCode && employeeCode !== row.candidateId
      ? `${name} (${employeeCode})`
      : `${name} (${row.candidateId || "-"})`,
    pendingWith:
      row.status === "draft"
        ? "HR / Recruiter"
        : row.pendingLabel || row.pendingRole || "-",
    pendingAt,
    status,
    onboardingRequestId: row.id,
    candidateId: row.candidateId,
    submittedAt: row.submittedAt || row.createdAt,
    approvedBy: status === "Approved" ? "System" : "-",
    when:
      status === "Approved" && row.completedAt
        ? new Date(row.completedAt).toLocaleString()
        : "-",
  };
}

function checklistTaskIdentity(task = {}) {
  const name = String(task.name || "task")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]+/g, "")
    .slice(0, 40);
  return name || "task";
}

export function mapOnboardingChecklistToDeskItem(row) {
  const task = row.checklistTask || {};
  const org = row.payload?.org || {};
  const name = row.candidateName || org.name || org.fullName || "New Employee";
  const empCode = row.employeeCode || row.candidateId || "-";
  const assigneeDisplay =
    row.assigneeDisplay || task.assigneeDisplay || task.actionRole || "HR";
  const workflowTrail = Array.isArray(row.workflowTrail)
    ? row.workflowTrail
    : Array.isArray(task.workflowTrail)
      ? task.workflowTrail
      : [];
  const taskKey = checklistTaskIdentity(task);

  return {
    requestId: row.deskItemKey || `onboarding_chk_${row.id}_${taskKey}`,
    panelKey: "approvals",
    actionLabel: resolveChecklistDisplayLabel({
      lifecycleKind: "onboarding",
      taskName: task.name,
      actionLabel: ONBOARDING_TASK_LABEL,
    }),
    deskMode: "checklist",
    onboardingRequestId: row.id,
    checklistTaskId: task.id ?? task.taskId ?? task.itemKey ?? null,
    checklistTask: task,
    checklistName: task.name || null,
    employeeCode: row.employeeCode || (empCode !== "-" ? empCode : null),
    linkedEmployeeId: row.employeeId || task.employeeId || null,
    source: "api",
    notifyOnlyFyi: false,
    checklistRejected: task.status === "rejected" || task.status === "sent_back",
    profileComplete: row.profileComplete === true,
    profileCompletionPercent: row.profileCompletionPercent ?? null,
    profilePendingSections: row.profilePendingSections || [],
    readOnlyApproval: task.status === "done",
    userId: row.createdByUserId,
    userName: name,
    userEmail: org.officialEmail || org.personalEmail || "",
    employeeId: empCode,
    status:
      task.status === "done"
        ? "Approved"
        : task.status === "rejected" || task.status === "sent_back"
          ? "Rejected"
          : "Pending",
    submittedAt: task.triggeredAt || row.submittedAt || row.completedAt,
    updatedAt: row.updatedAt,
    pendingWith: assigneeDisplay,
    assigneeDisplay,
    currentStep: null,
    totalSteps: null,
    formData: {
      taskName: task.name || "Onboarding task",
      dueAt: task.dueAt || "",
      completedComments: task.completedComments || "",
      sentBackComments: task.rejectedComments || task.sentBackComments || "",
    },
    workflowTrail,
  };
}

export function mapOnboardingRequestToDeskItem(row, options = {}) {
  const org = row.payload?.org || {};
  const notifyOnlyFyi = !!options.notifyOnlyFyi;
  const acknowledged = !!options.acknowledged;
  const alreadyOnboarded =
    notifyOnlyFyi &&
    (row.status === "completed" || row.workflowSnapshot?.notifyOnlyAutoApproved);
  const deskStatus = mapApiStatusToDeskStatus(row.status, {
    notifyOnlyFyi,
    acknowledged,
  });
  const workflowTrail = buildOnboardingWorkflowTrail(row);

  return {
    requestId: `onboarding_${row.id}${notifyOnlyFyi ? "_notify" : ""}`,
    panelKey: "approvals",
    actionLabel: ONBOARDING_APPROVAL_LABEL,
    onboardingRequestId: row.id,
    source: "api",
    deskMode: notifyOnlyFyi ? "notify" : row.deskMode || "approval",
    notifyOnlyFyi,
    alreadyOnboarded,
    readOnlyApproval: notifyOnlyFyi
      ? acknowledged
      : deskStatus !== "Pending",
    userId: row.createdByUserId,
    userName: org.name || org.fullName || "New Employee",
    userEmail: org.officialEmail || org.personalEmail || "",
    employeeId: row.candidateId,
    employeeCode: row.employeeCode || null,
    linkedEmployeeId: row.employeeId || null,
    profileComplete: row.profileComplete === true,
    profileCompletionPercent: row.profileCompletionPercent ?? null,
    profilePendingSections: row.profilePendingSections || [],
    canApprove:
      !notifyOnlyFyi && deskStatus === "Pending" && row.profileComplete === true,
    status: deskStatus,
    submittedAt: row.submittedAt || row.createdAt,
    updatedAt: row.updatedAt,
    pendingWith: notifyOnlyFyi
      ? acknowledged
        ? "Acknowledged (notification only)"
        : alreadyOnboarded
          ? "Already onboarded — FYI only (no approval)"
          : "Notification only — no approval action"
      : row.pendingAssigneeDisplay ||
        row.pendingLabel ||
        row.pendingRole ||
        "Awaiting workflow assignment",
    assigneeDisplay: notifyOnlyFyi
      ? "Notification"
      : row.pendingAssigneeDisplay || row.pendingLabel || row.pendingRole || "",
    pendingAssigneeDisplay: row.pendingAssigneeDisplay || null,
    pendingRole: row.pendingRole || null,
    pendingLabel: row.pendingLabel || null,
    canAct: !notifyOnlyFyi && deskStatus === "Pending",
    currentStep: row.currentStep,
    totalSteps: row.totalSteps,
    workflowSnapshot: row.workflowSnapshot || null,
    approvals: row.approvals || [],
    policyName: row.policySnapshot?.policyName || row.policySnapshot?.name || null,
    formData: buildOnboardingDeskFormData(org),
    workflowTrail: notifyOnlyFyi
      ? [
          {
            status: "Notification",
            role: "Notification",
            label: "N",
            displayText: "Notification",
            isNotification: true,
            actor: null,
            actedAt: null,
            reason: "",
            stepOrder: 1,
          },
        ]
      : workflowTrail,
    workflowSteps: notifyOnlyFyi
      ? [
          {
            status: "Notification",
            role: "Notification",
            label: "N",
            displayText: "Notification",
            isNotification: true,
            actor: null,
            actedAt: null,
            reason: "",
            stepOrder: 1,
          },
        ]
      : workflowTrail,
  };
}

export function mapOnboardingMyRequestToDeskItem(row) {
  const org = row.payload?.org || {};
  const name = row.candidateName || row.employeeName || org.name || org.fullName || "New Employee";
  const empCode = row.employeeCode || row.candidateId || "-";
  const apiStatus = String(row.status || row.apiStatus || "").toLowerCase();
  const deskStatus =
    row.deskStatus === "Approved"
      ? "Approved"
      : row.deskStatus === "Rejected"
        ? "Rejected"
        : "Pending";
  const workflowTrail = buildOnboardingWorkflowTrail(row);

  const pendingRoleLabel =
    row.pendingLabel ||
    (row.pendingRole ? String(row.pendingRole).replace(/_/g, " ") : "") ||
    "";
  const pendingPerson = row.pendingAssigneeDisplay || "";
  let pendingWith = "";
  if (apiStatus === "draft") {
    pendingWith = "Not submitted — open candidate and Submit for approval";
  } else {
    pendingWith =
      pendingPerson || pendingRoleLabel || "Awaiting workflow assignment";
  }

  const canAct = row.canAct === true || row.canApprove === true;
  const profileComplete = row.profileComplete !== false;
  const canApprove =
    deskStatus === "Pending" && apiStatus === "pending_approval" && canAct && profileComplete;

  // Ideal path: if you can act (RM / Admin), surface as an Approval — not a dead My Request.
  // My Request (Recall-only) is only for owners who are waiting on someone else, or drafts.
  const actionableApproval = canApprove || (canAct && deskStatus === "Pending" && apiStatus === "pending_approval");

  return {
    requestId: actionableApproval
      ? `onboarding_${row.id}`
      : `onboarding_req_${row.id}`,
    panelKey: actionableApproval ? "approvals" : "review",
    actionLabel: actionableApproval
      ? ONBOARDING_APPROVAL_LABEL
      : ONBOARDING_REQUEST_LABEL,
    onboardingRequestId: row.id,
    source: "api",
    deskMode: actionableApproval ? "approval" : "request",
    lifecycleKind: "onboarding",
    notifyOnlyFyi: false,
    readOnlyApproval: deskStatus !== "Pending",
    canApprove: actionableApproval && profileComplete,
    canAct: actionableApproval || canAct,
    userId: row.createdByUserId,
    userName: name,
    userEmail: org.officialEmail || org.personalEmail || "",
    employeeId: empCode,
    employeeCode: row.employeeCode || null,
    linkedEmployeeId: row.employeeId || null,
    profileComplete,
    profileCompletionPercent: row.profileCompletionPercent ?? null,
    profilePendingSections: row.profilePendingSections || [],
    status: deskStatus,
    apiStatus: row.status || row.apiStatus || null,
    submittedAt: row.submittedAt || row.createdAt,
    updatedAt: row.updatedAt,
    pendingWith,
    assigneeDisplay: pendingPerson || pendingRoleLabel || "",
    pendingAssigneeDisplay: row.pendingAssigneeDisplay || null,
    pendingRole: row.pendingRole || null,
    pendingLabel: row.pendingLabel || null,
    currentStep: row.currentStep,
    totalSteps: row.totalSteps,
    workflowSnapshot: row.workflowSnapshot || null,
    approvals: row.approvals || [],
    policyName: row.policySnapshot?.policyName || row.policySnapshot?.name || null,
    formData: buildOnboardingDeskFormData(org),
    workflowTrail,
    workflowSteps: workflowTrail,
  };
}

export function isOnboardingDeskRequest(request) {
  return (
    request?.source === "api" &&
    (request?.actionLabel === ONBOARDING_APPROVAL_LABEL ||
      request?.actionLabel === ONBOARDING_TASK_LABEL ||
      request?.actionLabel === ONBOARDING_REQUEST_LABEL ||
      request?.lifecycleKind === "onboarding") &&
    request?.onboardingRequestId != null
  );
}

export function isOnboardingChecklistDeskRequest(request) {
  return (
    isOnboardingDeskRequest(request) &&
    (request?.deskMode === "checklist" || request?.checklistTaskId != null)
  );
}
